import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import apiService from '../../utils/apiService';
import { toAmount } from '../../utils/dashboardWidgets';
import { getThemeColor } from '../../config/appConfig';
import './next-lot-rebate.css';

const THEME = getThemeColor('primary');
const THEME_DARK = getThemeColor('primary', 'dark');
const POLL_MS = 30 * 1000;
/** Backend may need a beat after periodEnd before EnsureAndProcess rolls the window. */
const ZERO_RETRY_DELAYS_MS = [0, 1000, 2500, 5000, 10000];

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== '') return obj[key];
  }
  return undefined;
};

const pad2 = (n) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

const formatMoney = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toAmount(value));

/** Match UI: -HH:MM:SS */
const formatCountdown = (totalSeconds) => {
  const sec = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  return `-${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
};

/**
 * Map GET /Dashboard/GetNextLotRebatePayment result
 * { title, totalHoldAmount, periodDisplay, remainingTotalSeconds, timerDisplay, ... }
 */
const parseNextLotRebate = (payload) => {
  const data = payload?.result ?? payload?.data ?? payload ?? {};
  if (!data || typeof data !== 'object') return null;

  const remainingTotalSeconds = Number(
    pick(data, 'remainingTotalSeconds', 'RemainingTotalSeconds')
  );
  const hours = Number(pick(data, 'remainingHours', 'RemainingHours'));
  const minutes = Number(pick(data, 'remainingMinutes', 'RemainingMinutes'));
  const seconds = Number(pick(data, 'remainingSeconds', 'RemainingSeconds'));

  let secondsLeft = null;
  if (Number.isFinite(remainingTotalSeconds) && remainingTotalSeconds >= 0) {
    secondsLeft = Math.floor(remainingTotalSeconds);
  } else if (
    Number.isFinite(hours) &&
    Number.isFinite(minutes) &&
    Number.isFinite(seconds)
  ) {
    secondsLeft = Math.max(0, hours * 3600 + minutes * 60 + seconds);
  }

  const timerDisplay = String(pick(data, 'timerDisplay', 'TimerDisplay') || '').trim();
  const releaseId = pick(
    data,
    'affiliateReleasePaymentId',
    'AffiliateReleasePaymentId',
    'userReleasePaymentId'
  );

  return {
    title: String(pick(data, 'title', 'Title') || 'Next Lot Rebate payment'),
    totalHoldAmount: toAmount(pick(data, 'totalHoldAmount', 'TotalHoldAmount')),
    periodDisplay: String(pick(data, 'periodDisplay', 'PeriodDisplay') || '').trim(),
    secondsLeft,
    releaseId: releaseId != null ? String(releaseId) : '',
    timerDisplay: timerDisplay
      ? timerDisplay.startsWith('-')
        ? timerDisplay
        : `-${timerDisplay}`
      : null,
  };
};

const NextLotRebateWidget = () => {
  const [title, setTitle] = useState('Next Lot Rebate payment');
  const [total, setTotal] = useState(0);
  const [periodDisplay, setPeriodDisplay] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [timerDisplay, setTimerDisplay] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const hitZeroRef = useRef(false);
  const zeroRetryTimersRef = useRef([]);
  const releaseIdRef = useRef('');

  const clearZeroRetries = useCallback(() => {
    zeroRetryTimersRef.current.forEach((id) => window.clearTimeout(id));
    zeroRetryTimersRef.current = [];
  }, []);

  const applyPayload = useCallback((raw) => {
    const parsed = parseNextLotRebate(raw);
    if (!parsed) return;
    setTitle(parsed.title);
    setTotal(parsed.totalHoldAmount);
    setPeriodDisplay(parsed.periodDisplay);
    setSecondsLeft(parsed.secondsLeft);
    setTimerDisplay(parsed.timerDisplay);
    setLoaded(true);

    const nextId = parsed.releaseId || '';
    const windowChanged = nextId && nextId !== releaseIdRef.current;
    releaseIdRef.current = nextId;

    // New positive window (or rolled period) → allow another zero-refetch later.
    if ((parsed.secondsLeft != null && parsed.secondsLeft > 0) || windowChanged) {
      hitZeroRef.current = false;
      clearZeroRetries();
    }
  }, [clearZeroRetries]);

  const loadRebate = useCallback(async () => {
    try {
      const res = await apiService.getNextLotRebatePayment();
      if (!res?.success) return null;
      applyPayload(res.data ?? res.result ?? res);
      return res;
    } catch {
      // keep last known values
      return null;
    }
  }, [applyPayload]);

  const handleTimerHitZero = useCallback(() => {
    // API EnsureAndProcess may need a moment after periodEnd — retry without full page reload.
    clearZeroRetries();
    zeroRetryTimersRef.current = ZERO_RETRY_DELAYS_MS.map((ms) =>
      window.setTimeout(() => {
        loadRebate();
      }, ms)
    );
  }, [loadRebate, clearZeroRetries]);

  useEffect(() => {
    loadRebate();
    const refresh = window.setInterval(loadRebate, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') loadRebate();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(refresh);
      document.removeEventListener('visibilitychange', onVisible);
      clearZeroRetries();
    };
  }, [loadRebate, clearZeroRetries]);

  useEffect(() => {
    if (secondsLeft == null) return undefined;

    const id = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev == null) return prev;
        const next = Math.max(0, prev - 1);
        if (prev > 0 && next <= 0 && !hitZeroRef.current) {
          hitZeroRef.current = true;
          window.setTimeout(() => handleTimerHitZero(), 0);
        }
        return next;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [secondsLeft == null, handleTimerHitZero]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show a space after the leading dash: "- 00:06:07".
  const countdown = (
    secondsLeft != null ? formatCountdown(secondsLeft) : timerDisplay || '-00:00:00'
  ).replace(/^-\s*/, '- ');

  return (
    <div
      className={`next-lot-rebate${loaded ? ' is-loaded' : ''}`}
      style={{
        '--nlr-primary': THEME,
        '--nlr-primary-dark': THEME_DARK,
      }}
      title={periodDisplay || undefined}
      aria-label={`${title}. Total ${formatMoney(total)}. ${countdown}. ${periodDisplay}`}
    >
      <p className="next-lot-rebate__title">{title}</p>
      <p className="next-lot-rebate__row">
        <span className="next-lot-rebate__total-label">TOTAL:</span>{' '}
        <span className="next-lot-rebate__amount">{formatMoney(total)}</span>{' '}
        <span className="next-lot-rebate__countdown">{countdown}</span>
      </p>
      {periodDisplay ? (
        <p className="next-lot-rebate__period">
          <CalendarDays className="next-lot-rebate__cal" aria-hidden="true" strokeWidth={2} />
          <span>{periodDisplay}</span>
        </p>
      ) : null}
    </div>
  );
};

export default NextLotRebateWidget;
